import axios, { AxiosPromise } from "axios";
import { dashboardApiConfig, ops } from "./config";
import {
  BulkItemIdsRequestDto,
  DataKitAssignmentDto,
  DataKitDiscardResultDto,
  DataKitDto,
  DataKitGroupDto,
  DataKitImportPreviewDto,
  DataKitImportRequestDto,
  DataKitImportResultResponseDto,
  DataKitItemDto,
  DataKitMenuDto,
  DataKitPublishDiffDto,
  DesignMenuRequestDto,
  DataKitPublishEventDto,
  DataKitPublishResultDto,
  ReparentGroupRequestDto,
  DataKitTagDto,
  FinalMenuPageDto,
  GroupMenuUsageDto,
  GroupNoteRequestDto,
  ImportParseDto,
  MembershipRequestDto,
  MenuUsageDto,
  MenuRequestDto,
  MenuDesignDto,
  MenuPriceRequestDto,
  MergeTagsRequestDto,
  RenameGroupRequestDto,
  RenameTagRequestDto,
  SetAvailabilityRequestDto,
  SubgroupRequestDto,
  UnassignPreviewDto,
  VenueItemDto,
  VenuePriceOverrideRequestDto,
} from "../domain/DataKitDto";

const base = () => `${dashboardApiConfig.basePath}/data-kits`;
const kit = (kitId: string) => `${base()}/${encodeURIComponent(kitId)}`;
const item = (itemId: string) => `${base()}/items/${encodeURIComponent(itemId)}`;

const DataKitApi = {
  listKits(includeArchived = false): AxiosPromise<DataKitDto[]> {
    return axios.get(`${base()}?includeArchived=${includeArchived}`, ops());
  },
  getKit(kitId: string): AxiosPromise<DataKitDto> {
    return axios.get(kit(kitId), ops());
  },
  createKit(dto: DataKitDto): AxiosPromise<DataKitDto> {
    return axios.post(base(), dto, ops());
  },
  updateKit(kitId: string, dto: DataKitDto): AxiosPromise<DataKitDto> {
    return axios.put(kit(kitId), dto, ops());
  },
  archiveKit(kitId: string): AxiosPromise<DataKitDto> {
    return axios.put(`${kit(kitId)}/archive`, {}, ops());
  },
  unarchiveKit(kitId: string): AxiosPromise<DataKitDto> {
    return axios.put(`${kit(kitId)}/unarchive`, {}, ops());
  },

  listItems(kitId: string, includeArchived = false): AxiosPromise<DataKitItemDto[]> {
    return axios.get(`${kit(kitId)}/items?includeArchived=${includeArchived}`, ops());
  },
  getItem(itemId: string): AxiosPromise<DataKitItemDto> {
    return axios.get(item(itemId), ops());
  },
  createItem(kitId: string, dto: DataKitItemDto): AxiosPromise<DataKitItemDto> {
    return axios.post(`${kit(kitId)}/items`, dto, ops());
  },
  updateItem(itemId: string, dto: DataKitItemDto): AxiosPromise<DataKitItemDto> {
    return axios.put(item(itemId), dto, ops());
  },
  archiveItem(itemId: string): AxiosPromise<DataKitItemDto> {
    return axios.put(`${item(itemId)}/archive`, {}, ops());
  },
  unarchiveItem(itemId: string): AxiosPromise<DataKitItemDto> {
    return axios.put(`${item(itemId)}/unarchive`, {}, ops());
  },
  deleteItem(itemId: string): AxiosPromise<void> {
    return axios.delete(item(itemId), ops());
  },

  addMembership(itemId: string, dto: MembershipRequestDto): AxiosPromise<DataKitItemDto> {
    return axios.post(`${item(itemId)}/groups`, dto, ops());
  },
  removeMembership(itemId: string, groupName: string): AxiosPromise<DataKitItemDto> {
    return axios.delete(`${item(itemId)}/groups?groupName=${encodeURIComponent(groupName)}`, ops());
  },

  reparentGroup(kitId: string, dto: ReparentGroupRequestDto): AxiosPromise<DataKitGroupDto> {
    return axios.put(`${kit(kitId)}/groups/reparent`, dto, ops());
  },

  setAvailability(kitId: string, dto: SetAvailabilityRequestDto): AxiosPromise<DataKitItemDto[]> {
    return axios.put(`${kit(kitId)}/items/bulk/availability`, dto, ops());
  },
  bulkArchiveItems(kitId: string, dto: BulkItemIdsRequestDto): AxiosPromise<DataKitItemDto[]> {
    return axios.put(`${kit(kitId)}/items/bulk/archive`, dto, ops());
  },
  bulkRestoreItems(kitId: string, dto: BulkItemIdsRequestDto): AxiosPromise<DataKitItemDto[]> {
    return axios.put(`${kit(kitId)}/items/bulk/unarchive`, dto, ops());
  },

  listMenus(): AxiosPromise<DataKitMenuDto[]> {
    return axios.get(`${base()}/menus`, ops());
  },
  listMenuDesigns(menuId: string): AxiosPromise<MenuDesignDto[]> {
    return axios.get(`${base()}/menus/${encodeURIComponent(menuId)}/designs`, ops());
  },
  listAttachableDesigns(): AxiosPromise<MenuDesignDto[]> {
    return axios.get(`${base()}/menus/designs/attachable`, ops());
  },
  createMenu(dto: MenuRequestDto): AxiosPromise<DataKitMenuDto> {
    return axios.post(`${base()}/menus`, dto, ops());
  },
  updateMenu(menuId: string, dto: MenuRequestDto): AxiosPromise<DataKitMenuDto> {
    return axios.put(`${base()}/menus/${encodeURIComponent(menuId)}`, dto, ops());
  },
  deleteMenu(menuId: string): AxiosPromise<void> {
    return axios.delete(`${base()}/menus/${encodeURIComponent(menuId)}`, ops());
  },
  setDesignMenu(designId: string, dto: DesignMenuRequestDto): AxiosPromise<void> {
    return axios.put(`${base()}/menus/designs/${encodeURIComponent(designId)}`, dto, ops());
  },
  setMenuPrice(itemId: string, dto: MenuPriceRequestDto): AxiosPromise<DataKitItemDto> {
    return axios.put(`${base()}/menus/items/${encodeURIComponent(itemId)}/price`, dto, ops());
  },
  listGroups(kitId: string): AxiosPromise<DataKitGroupDto[]> {
    return axios.get(`${kit(kitId)}/groups`, ops());
  },
  createGroup(kitId: string, dto: SubgroupRequestDto): AxiosPromise<DataKitGroupDto> {
    return axios.post(`${kit(kitId)}/groups`, dto, ops());
  },
  renameGroup(kitId: string, dto: RenameGroupRequestDto): AxiosPromise<DataKitGroupDto> {
    return axios.put(`${kit(kitId)}/groups/rename`, dto, ops());
  },
  setGroupNote(kitId: string, dto: GroupNoteRequestDto): AxiosPromise<DataKitGroupDto> {
    return axios.put(`${kit(kitId)}/groups/note`, dto, ops());
  },
  deleteGroup(kitId: string, name: string, path: string[] = []): AxiosPromise<void> {
    // `path` is repeatable, root first, and identifies a nested group unambiguously; `name` stays
    // as the single-level fallback the server uses when no path is sent.
    const parts = path.map((p) => `path=${encodeURIComponent(p)}`).join("&");
    const query = `name=${encodeURIComponent(name)}${parts ? `&${parts}` : ""}`;
    return axios.delete(`${kit(kitId)}/groups?${query}`, ops());
  },

  listTags(kitId: string): AxiosPromise<DataKitTagDto[]> {
    return axios.get(`${kit(kitId)}/tags`, ops());
  },
  autocompleteTags(kitId: string): AxiosPromise<string[]> {
    return axios.get(`${kit(kitId)}/tags/autocomplete`, ops());
  },
  renameTag(kitId: string, dto: RenameTagRequestDto): AxiosPromise<DataKitTagDto> {
    return axios.put(`${kit(kitId)}/tags/rename`, dto, ops());
  },
  mergeTags(kitId: string, dto: MergeTagsRequestDto): AxiosPromise<DataKitTagDto> {
    return axios.put(`${kit(kitId)}/tags/merge`, dto, ops());
  },

  listMenuUsage(kitId: string): AxiosPromise<MenuUsageDto[]> {
    return axios.get(`${kit(kitId)}/menus`, ops());
  },
  listFinalMenus(kitId: string, limit: number, offset: number): AxiosPromise<FinalMenuPageDto> {
    return axios.get(`${kit(kitId)}/menus/final?limit=${limit}&offset=${offset}`, ops());
  },
  listGroupMenuUsage(kitId: string): AxiosPromise<GroupMenuUsageDto[]> {
    return axios.get(`${kit(kitId)}/groups/menus`, ops());
  },

  getPublishDiff(kitId: string): AxiosPromise<DataKitPublishDiffDto> {
    return axios.get(`${kit(kitId)}/publish/diff`, ops());
  },
  publishKit(kitId: string): AxiosPromise<DataKitPublishResultDto> {
    return axios.post(`${kit(kitId)}/publish`, {}, ops());
  },
  discardKit(kitId: string): AxiosPromise<DataKitDiscardResultDto> {
    return axios.post(`${kit(kitId)}/discard`, {}, ops());
  },
  getDesignPublishEvents(designId: string): AxiosPromise<DataKitPublishEventDto[]> {
    return axios.get(`${base()}/designs/${encodeURIComponent(designId)}/publish-events`, ops());
  },

  listAssignments(kitId: string): AxiosPromise<DataKitAssignmentDto[]> {
    return axios.get(`${kit(kitId)}/assignments`, ops());
  },
  createAssignment(kitId: string, dto: DataKitAssignmentDto): AxiosPromise<DataKitAssignmentDto[]> {
    return axios.post(`${kit(kitId)}/assignments`, dto, ops());
  },
  previewUnassign(kitId: string, unitType: string, unitId: string): AxiosPromise<UnassignPreviewDto> {
    return axios.get(
      `${kit(kitId)}/assignments/preview-unassign?unitType=${encodeURIComponent(unitType)}&unitId=${encodeURIComponent(unitId)}`,
      ops()
    );
  },
  deleteAssignment(kitId: string, unitType: string, unitId: string): AxiosPromise<DataKitAssignmentDto[]> {
    return axios.delete(
      `${kit(kitId)}/assignments?unitType=${encodeURIComponent(unitType)}&unitId=${encodeURIComponent(unitId)}`,
      ops()
    );
  },

  setVenuePriceOverride(
    unitType: string,
    unitId: string,
    externalId: string,
    dto: VenuePriceOverrideRequestDto
  ): AxiosPromise<VenueItemDto> {
    return axios.put(
      `${base()}/venues/${encodeURIComponent(unitType)}/${encodeURIComponent(unitId)}/items/${encodeURIComponent(externalId)}/price`,
      dto,
      ops()
    );
  },
  listVenueItems(unitType: string, unitId: string): AxiosPromise<VenueItemDto[]> {
    return axios.get(
      `${base()}/venues/${encodeURIComponent(unitType)}/${encodeURIComponent(unitId)}/items`,
      ops()
    );
  },

  parseImport(kitId: string, file: File): AxiosPromise<ImportParseDto> {
    const form = new FormData();
    form.append("file", file);
    return axios.post(`${kit(kitId)}/import/parse`, form, ops());
  },
  previewImport(kitId: string, dto: DataKitImportRequestDto): AxiosPromise<DataKitImportPreviewDto> {
    return axios.post(`${kit(kitId)}/import/preview`, dto, ops());
  },
  applyImport(kitId: string, dto: DataKitImportRequestDto): AxiosPromise<DataKitImportResultResponseDto> {
    return axios.post(`${kit(kitId)}/import`, dto, ops());
  },
};

export default DataKitApi;
